// Geometry-only port of SimNIBS 4.6.0 calc_matsimnibs / triangle_normals(smooth=1).
// Upstream: simnibs/mesh_tools/mesh_io.py (GPL-3.0-or-later; license in ../fem).
// Input scalp is the external-air-relabelled surface used by the actual FEM poses.

const norm = (x,y,z) => Math.sqrt(x*x+y*y+z*z);

export function createCoilProjector(header, vertices, triangles, yReferenceMm) {
  const basis=header.basis, seed=basis?.seed_scalp_mm, matrix=basis?.seed_matrix;
  const gap=header.poses?.[0]?.scalp_distance_mm;
  if (!basis || !seed?.every(Number.isFinite) || !matrix || yReferenceMm?.length!==3 ||
      !yReferenceMm.every(Number.isFinite) || vertices.length!==header.arrays.scalp_vertices.shape[0]*3 ||
      triangles.length!==header.arrays.scalp_triangles.shape[0]*3 || !vertices.every(Number.isFinite) ||
      !Number.isFinite(gap) || gap<0 || header.poses.some(p=>p.scalp_distance_mm!==gap))
    throw new Error('Invalid native scalp / coil rule');
  const nv=vertices.length/3, nt=triangles.length/3;
  if (!triangles.every(i=>Number.isInteger(i)&&i>=0&&i<nv)) throw new Error('Invalid scalp triangles');
  const triNormals=new Float64Array(nt*3), nodeNormals=new Float64Array(nv*3);
  const smooth=new Float64Array(nt*3), boxes=new Float64Array(nt*6);
  for(let t=0;t<nt;t++) {
    const i=triangles[t*3]*3,j=triangles[t*3+1]*3,k=triangles[t*3+2]*3;
    const ax=vertices[j]-vertices[i],ay=vertices[j+1]-vertices[i+1],az=vertices[j+2]-vertices[i+2];
    const bx=vertices[k]-vertices[i],by=vertices[k+1]-vertices[i+1],bz=vertices[k+2]-vertices[i+2];
    triNormals.set([ay*bz-az*by,az*bx-ax*bz,ax*by-ay*bx],t*3);
    if(norm(...triNormals.subarray(t*3,t*3+3))===0) throw new Error('Degenerate scalp triangle');
    for(let c=0;c<3;c++) { boxes[t*6+c]=Math.min(vertices[i+c],vertices[j+c],vertices[k+c]); boxes[t*6+c+3]=Math.max(vertices[i+c],vertices[j+c],vertices[k+c]); }
  }
  // Match upstream's two element-to-node passes for smooth=1, including its
  // initial area weighting and final normalized vertex-to-face average.
  for(let pass=0;pass<2;pass++) {
    nodeNormals.fill(0);
    for(let t=0;t<nt;t++) for(let corner=0;corner<3;corner++) {
      const n=triangles[t*3+corner]*3;
      for(let c=0;c<3;c++) nodeNormals[n+c]+=triNormals[t*3+c];
    }
    for(let t=0;t<nt;t++) {
      const i=triangles[t*3]*3,j=triangles[t*3+1]*3,k=triangles[t*3+2]*3;
      const x=nodeNormals[i]+nodeNormals[j]+nodeNormals[k],y=nodeNormals[i+1]+nodeNormals[j+1]+nodeNormals[k+1],z=nodeNormals[i+2]+nodeNormals[j+2]+nodeNormals[k+2];
      const d=norm(x,y,z);
      triNormals.set([x/d,y/d,z/d],t*3);
    }
  }
  for(let i=0;i<nv;i++) {
    const n=norm(nodeNormals[i*3],nodeNormals[i*3+1],nodeNormals[i*3+2]);
    if(n>0) for(let c=0;c<3;c++) nodeNormals[i*3+c]/=n;
  }
  for(let t=0;t<nt;t++) {
    const i=triangles[t*3]*3,j=triangles[t*3+1]*3,k=triangles[t*3+2]*3;
    const x=nodeNormals[i]+nodeNormals[j]+nodeNormals[k],y=nodeNormals[i+1]+nodeNormals[j+1]+nodeNormals[k+1],z=nodeNormals[i+2]+nodeNormals[j+2]+nodeNormals[k+2],d=norm(x,y,z);
    smooth.set([x/d,y/d,z/d],t*3);
  }
  const scratch=new Float64Array(3), closest=new Float64Array(3);
  function project(px,py,pz) {
    let distance=Infinity, face=-1;
    for(let t=0;t<nt;t++) {
      const b=t*6;
      const dx=Math.max(boxes[b]-px,0,px-boxes[b+3]),dy=Math.max(boxes[b+1]-py,0,py-boxes[b+4]),dz=Math.max(boxes[b+2]-pz,0,pz-boxes[b+5]);
      if(dx*dx+dy*dy+dz*dz>distance) continue;
      closestPoint(vertices,triangles,t,px,py,pz,scratch);
      const d=(scratch[0]-px)**2+(scratch[1]-py)**2+(scratch[2]-pz)**2;
      if(d<distance) {distance=d;face=t;closest.set(scratch);}
    }
    return face;
  }
  return {
    // Invert a scalp pick into the seed tangent coordinates by intersecting the
    // picked face's normal with the seed plane. The app explicitly clamps or
    // rejects these returned coordinates; this method does not clamp silently.
    offsetFromScalpPoint(point) {
      if(point?.length!==3 || !point.every(Number.isFinite)) throw new Error('Expected finite scalp RAS mm');
      const face=project(...point),a=triangles[face*3]*3,b=triangles[face*3+1]*3,c=triangles[face*3+2]*3;
      const ux=vertices[b]-vertices[a],uy=vertices[b+1]-vertices[a+1],uz=vertices[b+2]-vertices[a+2];
      const vx=vertices[c]-vertices[a],vy=vertices[c+1]-vertices[a+1],vz=vertices[c+2]-vertices[a+2];
      const n=[uy*vz-uz*vy,uz*vx-ux*vz,ux*vy-uy*vx];
      const denominator=n.reduce((s,v,c)=>s+v*matrix[c][2],0);
      if(Math.abs(denominator)<1e-10) throw new RangeError('Scalp face is parallel to the placement projection');
      const distance=seed.reduce((s,v,c)=>s+(v-closest[c])*matrix[c][2],0)/denominator;
      const q=closest.map((v,c)=>v+distance*n[c]-seed[c]);
      return [q.reduce((s,v,c)=>s+v*matrix[c][0],0),q.reduce((s,v,c)=>s+v*matrix[c][1],0)];
    },
    // Same tangent-plane query rule as the FEM. Output is engine column-major.
    pose(x,y,out=new Float32Array(16)) {
      const r=basis.valid_rectangle_mm;
      if(!Number.isFinite(x)||!Number.isFinite(y)||x<r[0][0]||x>r[0][1]||y<r[1][0]||y>r[1][1]) throw new RangeError('Coil is outside the validated patch');
      if(!(out instanceof Float32Array)||out.length!==16) throw new Error('Pose needs 16 float32 values');
      const face=project(...seed.map((v,c)=>v+x*matrix[c][0]+y*matrix[c][1]));
      const zx=-smooth[face*3],zy=-smooth[face*3+1],zz=-smooth[face*3+2];
      let yx=yReferenceMm[0]-closest[0],yy=yReferenceMm[1]-closest[1],yz=yReferenceMm[2]-closest[2];
      const d=yx*zx+yy*zy+yz*zz; yx-=d*zx; yy-=d*zy; yz-=d*zz;
      const n=norm(yx,yy,yz); if(n<1e-10) throw new Error('Degenerate coil handle direction');
      yx/=n; yy/=n; yz/=n;
      out.set([yy*zz-yz*zy,yz*zx-yx*zz,yx*zy-yy*zx,0,yx,yy,yz,0,zx,zy,zz,0,
        closest[0]-gap*zx,closest[1]-gap*zy,closest[2]-gap*zz,1]);
      return out;
    },
  };
}

// Closest point on a triangle by its Voronoi regions; no external 3D runtime.
function closestPoint(v,tri,t,px,py,pz,out) {
  const ai=tri[t*3]*3,bi=tri[t*3+1]*3,ci=tri[t*3+2]*3;
  const ax=v[ai],ay=v[ai+1],az=v[ai+2],bx=v[bi],by=v[bi+1],bz=v[bi+2],cx=v[ci],cy=v[ci+1],cz=v[ci+2];
  const abx=bx-ax,aby=by-ay,abz=bz-az,acx=cx-ax,acy=cy-ay,acz=cz-az;
  const apx=px-ax,apy=py-ay,apz=pz-az,d1=abx*apx+aby*apy+abz*apz,d2=acx*apx+acy*apy+acz*apz;
  if(d1<=0&&d2<=0) {out.set([ax,ay,az]);return;}
  const bpx=px-bx,bpy=py-by,bpz=pz-bz,d3=abx*bpx+aby*bpy+abz*bpz,d4=acx*bpx+acy*bpy+acz*bpz;
  if(d3>=0&&d4<=d3) {out.set([bx,by,bz]);return;}
  const vc=d1*d4-d3*d2;
  if(vc<=0&&d1>=0&&d3<=0) {const u=d1/(d1-d3);out.set([ax+u*abx,ay+u*aby,az+u*abz]);return;}
  const cpx=px-cx,cpy=py-cy,cpz=pz-cz,d5=abx*cpx+aby*cpy+abz*cpz,d6=acx*cpx+acy*cpy+acz*cpz;
  if(d6>=0&&d5<=d6) {out.set([cx,cy,cz]);return;}
  const vb=d5*d2-d1*d6;
  if(vb<=0&&d2>=0&&d6<=0) {const u=d2/(d2-d6);out.set([ax+u*acx,ay+u*acy,az+u*acz]);return;}
  const va=d3*d6-d5*d4;
  if(va<=0&&(d4-d3)>=0&&(d5-d6)>=0) {const u=(d4-d3)/((d4-d3)+(d5-d6));out.set([bx+u*(cx-bx),by+u*(cy-by),bz+u*(cz-bz)]);return;}
  const inverse=1/(va+vb+vc),u=vb*inverse,w=vc*inverse;
  out.set([ax+abx*u+acx*w,ay+aby*u+acy*w,az+abz*u+acz*w]);
}

export function createDipoleMesh(positions, radiusMm=0.65) {
  if(!(positions instanceof Float32Array)||positions.length%3||!positions.every(Number.isFinite)||!Number.isFinite(radiusMm)||radiusMm<=0) throw new Error('Invalid dipole glyphs');
  const dirs=[[1,0,0],[-1,0,0],[0,1,0],[0,-1,0],[0,0,1],[0,0,-1]];
  const faces=[0,2,4,2,1,4,1,3,4,3,0,4,2,0,5,1,2,5,3,1,5,0,3,5];
  const n=positions.length/3, vertices=new Float32Array(n*18),normals=new Float32Array(n*18),indices=new Uint32Array(n*24);
  for(let i=0;i<n;i++) {
    for(let j=0;j<6;j++) for(let c=0;c<3;c++) {vertices[i*18+j*3+c]=positions[i*3+c]+radiusMm*dirs[j][c];normals[i*18+j*3+c]=dirs[j][c];}
    for(let k=0;k<24;k++) indices[i*24+k]=i*6+faces[k];
  }
  return {positions:vertices,normals,indices,metadata:{provenance:'derived',source:'coil_positions',
    units:'mm',glyph:{shape:'octahedron',radius_mm:radiusMm,provenance:'artist-made'},
    description:'Electrical source locations; glyph radius is a visual convention, not device geometry.'}};
}
